import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api from '../api';

const BlogManagePage = () => {
    const [blogs, setBlogs] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedClass, setSelectedClass] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        summary: '',
        class: '',
        isPublished: false,
        tags: '',
        category: 'theory',
        coverImage: ''
    });

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            // Handle both old format (array) and new paginated format
            const classesData = res.data.success && res.data.classes
                ? res.data.classes.data
                : res.data;
            setClasses(Array.isArray(classesData) ? classesData : []);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchBlogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = selectedClass ? `?classId=${selectedClass}` : '';
            const res = await api.get(`/blogs/admin/all${params}`);
            // Handle paginated response - blogs endpoint already has pagination
            const blogsData = res.data.blogs?.data || res.data.blogs || [];
            setBlogs(Array.isArray(blogsData) ? blogsData : []);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    }, [selectedClass]);

    useEffect(() => {
        fetchClasses();
        fetchBlogs();
    }, [selectedClass, fetchBlogs]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            const submitData = {
                ...formData,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
            };

            if (editingBlog) {
                await api.put(`/blogs/${editingBlog._id}`, submitData);
                alert('Blog updated successfully!');
            } else {
                await api.post('/blogs', submitData);
                alert('Blog created successfully!');
            }

            resetForm();
            fetchBlogs();
        } catch (error) {
            console.error('Error saving blog:', error);
            alert('Error saving blog. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (blog) => {
        setEditingBlog(blog);
        setFormData({
            title: blog.title,
            content: blog.content,
            summary: blog.summary || '',
            class: blog.class._id,
            isPublished: blog.isPublished,
            tags: blog.tags.join(', '),
            category: blog.category,
            coverImage: blog.coverImage || ''
        });
        setShowCreateForm(true);
    };

    const handleDelete = async (blogId) => {
        if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
            try {
                setLoading(true);
                await api.delete(`/blogs/${blogId}`);
                alert('Blog deleted successfully!');
                fetchBlogs();
            } catch (error) {
                console.error('Error deleting blog:', error);
                alert('Error deleting blog. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (5MB)
        if (file.size > 5000000) {
            alert('File size must be less than 5MB');
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, JPG, PNG, GIF, WebP)');
            return;
        }

        try {
            setLoading(true);
            const uploadFormData = new FormData();
            uploadFormData.append('coverImage', file);

            const res = await api.post('/blogs/upload-cover', uploadFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setFormData({ ...formData, coverImage: res.data.imageUrl });
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Error uploading image. Please try again.');
        } finally {
            setLoading(false);
            e.target.value = '';
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            content: '',
            summary: '',
            class: '',
            isPublished: false,
            tags: '',
            category: 'theory',
            coverImage: ''
        });
        setEditingBlog(null);
        setShowCreateForm(false);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryColor = (category) => {
        const colors = {
            theory: 'bg-blue-100 text-blue-800',
            practice: 'bg-green-100 text-green-800',
            examples: 'bg-yellow-100 text-yellow-800',
            exercises: 'bg-purple-100 text-purple-800',
            news: 'bg-red-100 text-red-800'
        };
        return colors[category] || 'bg-gray-100 text-gray-800';
    };

    // Math preview component
    const MathPreview = ({ text, className = '' }) => {
        if (!text) return null;
        return (
            <div className={`bg-slate-50 border border-slate-200 rounded-lg p-3 ${className}`}>
                <div className="prose prose-sm max-w-none">
                    <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                    >
                        {text.substring(0, 200) + (text.length > 200 ? '...' : '')}
                    </ReactMarkdown>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-semibold text-slate-900">Quản lý blog lý thuyết</h2>
                    <p className="text-slate-600">Tạo và quản lý bài viết lý thuyết cho từng lớp học</p>
                </div>
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 transition-colors"
                    disabled={loading}
                >
                    {showCreateForm ? 'Đóng form' : '+ Tạo bài viết mới'}
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-slate-700">Lọc theo lớp:</label>
                    <select
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="">Tất cả lớp</option>
                        {classes.map((cls) => (
                            <option key={cls._id} value={cls._id}>
                                {cls.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Create/Edit Form */}
            {showCreateForm && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                        {editingBlog ? 'Chỉnh sửa bài viết' : 'Tạo bài viết mới'}
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Tiêu đề *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                    placeholder="Nhập tiêu đề bài viết"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Lớp học *</label>
                                <select
                                    value={formData.class}
                                    onChange={(e) => setFormData({ ...formData, class: e.target.value })}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                    required
                                >
                                    <option value="">Chọn lớp học</option>
                                    {classes.map((cls) => (
                                        <option key={cls._id} value={cls._id}>
                                            {cls.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Tóm tắt</label>
                            <textarea
                                value={formData.summary}
                                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                rows="2"
                                placeholder="Viết tóm tắt ngắn gọn về bài viết (tối đa 200 ký tự)"
                                maxLength={200}
                            />
                            <div className="text-xs text-slate-500 mt-1">
                                {formData.summary.length}/200 ký tự
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Nội dung *</label>
                            <textarea
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                rows="10"
                                placeholder="Nhập nội dung bài viết (hỗ trợ Markdown và LaTeX)"
                                required
                            />
                            <div className="text-xs text-slate-500 mt-2">
                                Hỗ trợ Markdown và LaTeX. Ví dụ: **in đậm**, *in nghiêng*, $x^2$, $$\int x dx$$
                            </div>
                            {formData.content && (
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Xem trước:</label>
                                    <MathPreview text={formData.content} />
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Danh mục</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                >
                                    <option value="theory">Lý thuyết</option>
                                    <option value="practice">Thực hành</option>
                                    <option value="examples">Ví dụ</option>
                                    <option value="exercises">Bài tập</option>
                                    <option value="news">Tin tức</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Tags</label>
                                <input
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                                    placeholder="tag1, tag2, tag3"
                                />
                                <div className="text-xs text-slate-500 mt-1">
                                    Phân cách bằng dấu phẩy
                                </div>
                            </div>

                            <div className="flex items-center">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.isPublished}
                                        onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="ml-2 text-sm font-semibold text-slate-700">Xuất bản</span>
                                </label>
                            </div>
                        </div>

                        {/* Cover Image Upload */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Ảnh bìa</label>
                            {formData.coverImage ? (
                                <div className="relative">
                                    <img
                                        src={`http://localhost:5001${formData.coverImage}`}
                                        alt="Cover"
                                        className="max-w-full h-auto max-h-48 rounded-lg border border-slate-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, coverImage: '' })}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        id="cover-upload"
                                    />
                                    <label htmlFor="cover-upload" className="cursor-pointer">
                                        <div className="text-slate-400 mb-2 text-2xl">📷</div>
                                        <div className="text-sm text-slate-600">
                                            <span className="text-indigo-600 hover:text-indigo-700 font-medium">
                                                Nhấp để tải lên ảnh bìa
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-400 mt-1">
                                            PNG, JPG, GIF tối đa 5MB
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                            >
                                {loading ? 'Đang lưu...' : editingBlog ? 'Cập nhật' : 'Tạo bài viết'}
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-6 py-3 bg-slate-500 text-white rounded-xl hover:bg-slate-600 transition-colors"
                            >
                                Hủy
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Blog List */}
            <div className="space-y-4">
                {loading && blogs.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-lg text-slate-600">Đang tải...</div>
                    </div>
                ) : blogs.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📝</div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-2">Chưa có bài viết nào</h3>
                        <p className="text-slate-600 mb-4">Tạo bài viết lý thuyết đầu tiên cho lớp học.</p>
                    </div>
                ) : (
                    blogs.map((blog) => (
                        <div key={blog._id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-lg font-semibold text-slate-900">{blog.title}</h3>
                                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(blog.category)}`}>
                                            {blog.category === 'theory' ? 'Lý thuyết' :
                                                blog.category === 'practice' ? 'Thực hành' :
                                                    blog.category === 'examples' ? 'Ví dụ' :
                                                        blog.category === 'exercises' ? 'Bài tập' : 'Tin tức'}
                                        </span>
                                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${blog.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {blog.isPublished ? 'Đã xuất bản' : 'Bản nháp'}
                                        </span>
                                    </div>

                                    {blog.summary && (
                                        <p className="text-slate-600 mb-3">{blog.summary}</p>
                                    )}

                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            📚 {blog.class.name}
                                        </span>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            👁️ {blog.readCount} lượt đọc
                                        </span>
                                        {blog.tags.map((tag) => (
                                            <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="text-xs text-slate-500">
                                        Tạo: {formatDateTime(blog.createdAt)} |
                                        Xuất bản: {formatDateTime(blog.publishedAt)} |
                                        Tác giả: {blog.author?.name || 'Unknown'}
                                    </div>
                                </div>

                                <div className="flex gap-2 ml-4">
                                    <button
                                        onClick={() => handleEdit(blog)}
                                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                        disabled={loading}
                                    >
                                        Sửa
                                    </button>
                                    <button
                                        onClick={() => handleDelete(blog._id)}
                                        className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                        disabled={loading}
                                    >
                                        Xóa
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default BlogManagePage;